/**
 * !!!!!!!!!!!!!
 * ! IMPORTANT !
 * !!!!!!!!!!!!!
 *
 * This file and any files it imports MUST NOT depend on any data access files
 * such as permissions-info, request-info, nor deploy-info.
 */
import {safeStringify, type Metadata} from "@khanacademy/wonder-stuff-core";
import {
    KindSentryError,
    type SentryContext,
    type SentryData,
} from "@khanacademy/wonder-stuff-sentry";

import {Errors, type ErrorKind} from "./types.ts";

interface Options {
    /**
     * A cause responsible for the error being created.
     *
     * @type {?(string | Error)}
     */
    cause?: string | Error | null | undefined;
    /**
     * Data to be attached to the error that would be reported to Sentry.
     *
     * @type {?$Partial<SentryData>}
     */
    sentryData?: Partial<SentryData> | null | undefined;
    /**
     * Data to be attached to the error that will not get reported to Sentry.
     *
     * @type {?Metadata}
     */
    metadata?: Metadata | null | undefined;
    /**
     * The number of stack frames to strip from the error.
     *
     * @type {?number}
     */
    stripStackFrames?: number | null | undefined;
    /**
     * The minimum number of stack frames to try and retain.
     *
     * @type {?number}
     */
    minimumFrameCount?: number | null | undefined;
}

/**
 * Error to be thrown and logged within KA code.
 *
 * This custom error supports our error taxonomy.
 */
export class KAError extends KindSentryError {
    constructor(
        message: string,
        kind: ErrorKind,
        options: Options = Object.freeze({}),
    ) {
        const {cause, ...otherOptions} = options;
        if (Errors[kind] == null) {
            /**
             * We should not get here, other than during development if
             * new kinds are added or existing ones are changed. This is just
             * to validate inputs at the point TypeScript is not running.
             */
            throw new KAError(
                `Unrecognized error taxonomy: ${kind}`,
                Errors.InvalidInput,
                {cause},
            );
        }

        // TODO(somewhatabstract): When Wonder Stuff Sentry has ambiguous
        // error normalization, we can leverage that for getting the cause
        // into a usuable shape. Also, when we have error building support,
        // this will be a lot nicer.
        if (cause != null && !(cause instanceof Error)) {
            // Then whatever we have, we should save it into our sentry
            // metadata.
            const sentryData: Partial<SentryData> =
                otherOptions.sentryData ?? ({} as any);
            const contexts =
                sentryData.contexts ?? ({} as Record<string, SentryContext>);
            contexts.kaErrorAddendum = {
                nonErrorCause: safeStringify(cause),
            };
            sentryData.contexts = contexts;
            otherOptions.sentryData = sentryData;
        }

        super(message, kind, {
            ...otherOptions,
            cause: cause instanceof Error ? cause : undefined,
            prefix: "KA",
            name: "",
        });
    }

    get errorKind(): ErrorKind {
        // We know this is correct as we checked it in the constructor
        // and we're not really concerned about malicious modification.
        return this.kind as ErrorKind;
    }

    // TODO(somewhatabstract, FEI-3998): Once sentry metadata is supported,
    // add static method for building KAError from ambigous input as per
    // https://github.com/Khan/webapp/blob/84b6c3eba4911a2350d23c04a57de301f75fff79/apps/khanacademy/src/logging/internal/sentry.js#L249-L278
    // and/or apps/khanacademy/src/logging/internal/to-ka-error.js (possibly combine both)

    // The above two things should then make it easier to standardize rejections
    // where folks aren't sure what type their rejection is (string, error, event,
    // etc.), but we'll get a better stack trace in the error than one we create
    // really really late.
}
